import { useEffect, useState } from 'react';
import { type Health, healthSchema } from '@xfile/shared';
import { withTimeoutSignal } from './timeout';

/**
 * État de l'appel `/api/health` partagé : `null` tant que la requête n'a pas abouti,
 * `Health` une fois validé par Zod, `error` si l'API est injoignable ou la réponse
 * invalide.
 */
export interface HealthState {
  health: Health | null;
  error: boolean;
}

/**
 * SOURCE UNIQUE de `/api/health` pour toute l'app.
 *
 * La promesse vit au niveau du MODULE et est mémoïsée : quel que soit le nombre de
 * composants qui appellent `useHealth`, `/api/health` n'est demandé QU'UNE FOIS.
 * (Auparavant chaque appel créait son propre `useEffect` + `fetch` — quatre
 * consommateurs simultanés au démarrage, donc quatre requêtes, alors même que la
 * réponse est identique pour tous. Cette rafale aggrave le risque de 429 au
 * redémarrage du navigateur, quand tous les onglets se restaurent d'un coup.)
 *
 * ⚠ La requête n'est JAMAIS annulée au démontage d'un abonné : elle est partagée,
 * l'annuler priverait les autres de leur résultat. On se contente de ne plus
 * notifier les abonnés partis.
 */

let pending: Promise<HealthState> | null = null;
let snapshot: HealthState = { health: null, error: false };
const subscribers = new Set<(state: HealthState) => void>();

function publish(state: HealthState): void {
  snapshot = state;
  for (const notify of subscribers) notify(state);
}

/** Lance (au plus une fois) la requête partagée et publie son résultat. */
function ensureHealthRequest(): Promise<HealthState> {
  pending ??= fetch('/api/health', { signal: withTimeoutSignal(null) ?? null })
    .then((r) => r.json())
    .then((data) => {
      const state: HealthState = { health: healthSchema.parse(data), error: false };
      publish(state);
      return state;
    })
    .catch(() => {
      const state: HealthState = { health: null, error: true };
      publish(state);
      return state;
    });
  return pending;
}

/** Réinitialise la source partagée. RÉSERVÉ AUX TESTS (isolation entre cas). */
export function resetHealthCache(): void {
  pending = null;
  snapshot = { health: null, error: false };
  subscribers.clear();
}

/**
 * Récupère `/api/health` (une seule requête pour toute l'application) et re-parse
 * la réponse via `healthSchema`. Réutilisable sans dupliquer le fetch.
 */
export function useHealth(): HealthState {
  const [state, setState] = useState<HealthState>(snapshot);

  useEffect(() => {
    let subscribed = true;
    const notify = (next: HealthState): void => {
      if (subscribed) setState(next);
    };
    subscribers.add(notify);
    // Résultat déjà connu (requête antérieure) → adoption immédiate, sans refetch.
    void ensureHealthRequest().then(notify);
    return () => {
      subscribed = false;
      subscribers.delete(notify);
    };
  }, []);

  return state;
}
